import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { Component } from 'react';
import { Button } from '../button';
import { cn } from '../../utils/cn';
export class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.resetTimeoutId = null;
        this.previousResetKeys = [];
        this.resetErrorBoundary = () => {
            if (this.resetTimeoutId) {
                clearTimeout(this.resetTimeoutId);
                this.resetTimeoutId = null;
            }
            this.setState({
                hasError: false,
                error: null,
                errorInfo: null,
            });
        };
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            errorCount: 0,
        };
    }
    static getDerivedStateFromError(error) {
        return {
            hasError: true,
            error,
        };
    }
    componentDidCatch(error, errorInfo) {
        const { onError } = this.props;
        // Log error to console in development
        if (process.env.NODE_ENV === 'development') {
            console.error('Error caught by ErrorBoundary:', error, errorInfo);
        }
        // Call custom error handler if provided
        if (onError) {
            onError(error, errorInfo);
        }
        this.setState((prevState) => ({
            errorInfo,
            errorCount: prevState.errorCount + 1,
        }));
        // Auto-retry after 5 seconds for first 3 errors
        if (this.state.errorCount < 3) {
            this.resetTimeoutId = setTimeout(() => {
                this.resetErrorBoundary();
            }, 5000);
        }
    }
    componentDidUpdate(prevProps) {
        const { resetKeys, resetOnPropsChange } = this.props;
        const { hasError } = this.state;
        // Reset when resetKeys change
        if (hasError && resetKeys) {
            const hasResetKeyChanged = resetKeys.some((key, index) => key !== this.previousResetKeys[index]);
            if (hasResetKeyChanged) {
                this.resetErrorBoundary();
                this.previousResetKeys = [...resetKeys];
            }
        }
        // Reset when props change (if enabled)
        if (hasError && resetOnPropsChange && prevProps !== this.props) {
            this.resetErrorBoundary();
        }
    }
    componentWillUnmount() {
        if (this.resetTimeoutId) {
            clearTimeout(this.resetTimeoutId);
        }
    }
    render() {
        const { hasError, error, errorInfo, errorCount } = this.state;
        const { children, fallback, level = 'component', showDetails = false, isolate = true } = this.props;
        if (hasError && error) {
            // Custom fallback
            if (fallback) {
                return _jsx(_Fragment, { children: fallback });
            }
            // Default error UI based on level
            return (_jsx(ErrorFallback, { error: error, errorInfo: errorInfo, level: level, showDetails: showDetails, errorCount: errorCount, onReset: this.resetErrorBoundary, isolate: isolate }));
        }
        return children;
    }
}
const ErrorFallback = ({ error, errorInfo, level, showDetails, errorCount, onReset, isolate, }) => {
    const [detailsExpanded, setDetailsExpanded] = React.useState(false);
    const getLevelStyles = () => {
        switch (level) {
            case 'page':
                return 'min-h-screen';
            case 'section':
                return 'min-h-[400px]';
            case 'component':
                return 'min-h-[200px]';
            default:
                return '';
        }
    };
    const getErrorIcon = () => {
        switch (level) {
            case 'page':
                return (_jsx("svg", { className: "w-16 h-16 text-red-500", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" }) }));
            case 'section':
                return (_jsx("svg", { className: "w-12 h-12 text-yellow-500", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" }) }));
            default:
                return (_jsx("svg", { className: "w-8 h-8 text-gray-500", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" }) }));
        }
    };
    return (_jsx("div", { className: cn('flex items-center justify-center p-4', getLevelStyles(), isolate && 'bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700'), children: _jsxs("div", { className: "text-center max-w-md", children: [_jsx("div", { className: "flex justify-center mb-4", children: getErrorIcon() }), _jsx("h2", { className: "text-xl font-semibold text-gray-900 dark:text-white mb-2", children: level === 'page' ? 'Something went wrong' : 'Component Error' }), _jsx("p", { className: "text-gray-600 dark:text-gray-400 mb-4", children: level === 'page'
                        ? 'An unexpected error occurred. Please try refreshing the page.'
                        : 'This component encountered an error and cannot be displayed.' }), errorCount > 1 && (_jsxs("p", { className: "text-sm text-yellow-600 dark:text-yellow-400 mb-4", children: ["This error has occurred ", errorCount, " times."] })), _jsxs("div", { className: "flex gap-2 justify-center", children: [_jsx(Button, { onClick: onReset, variant: "default", size: "sm", children: "Try Again" }), level === 'page' && (_jsx(Button, { onClick: () => window.location.href = '/', variant: "outline", size: "sm", children: "Go Home" })), showDetails && (_jsxs(Button, { onClick: () => setDetailsExpanded(!detailsExpanded), variant: "ghost", size: "sm", children: [detailsExpanded ? 'Hide' : 'Show', " Details"] }))] }), showDetails && detailsExpanded && (_jsx("div", { className: "mt-4 text-left", children: _jsxs("div", { className: "bg-gray-100 dark:bg-gray-800 rounded-lg p-4 overflow-auto max-h-60", children: [_jsx("p", { className: "text-sm font-mono text-red-600 dark:text-red-400 mb-2", children: error.toString() }), errorInfo && (_jsx("pre", { className: "text-xs text-gray-600 dark:text-gray-400 overflow-x-auto", children: errorInfo.componentStack }))] }) }))] }) }));
};
// Higher-order component for wrapping components with error boundary
export function withErrorBoundary(Component, errorBoundaryProps) {
    const WrappedComponent = (props) => (_jsx(ErrorBoundary, { ...errorBoundaryProps, children: _jsx(Component, { ...props }) }));
    WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
    return WrappedComponent;
}
